import React, { useCallback, useContext, useEffect, useState } from 'react';

// React flow
import ReactFlow, { Background, ControlButton, Controls, ReactFlowProvider } from 'reactflow';
import 'reactflow/dist/style.css';

// Util
import uuid from 'uuid';
import { useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import { getCanvasWithAINotesBorderSet } from '../../../utils/AIUtil';
import { createSaveEvent } from '../../../utils/createSaveEvent';
import { send_request } from '../../../utils/Request';
import { saveCanvasEvent } from '../../../utils/canvasApi';

// Canvas
import './Canvas.css';
import { CanvasContext } from './WorkflowCanvas';
import {
  areSetsEqual,
  CANVAS_MIN_ZOOM_OUT,
  CanvasMinimap,
  DEFAULT_VIEWPORT,
  edgeTypes,
  extractEdges,
  extractNodes,
  getElements,
  isBackwardConnection,
  isInvalidBranchPathsConnection,
  isInvalidConvergeThreadsConnection,
  isInvalidNestedLoopConnection,
  isSameLink,
  isValidConnection,
  proOptions,
} from '../../../utils/CanvasUtil';

// Custom components
import CanvasWithRFInstance from './CanvasWithRFInstance';
import LoopConfigDialog from './LoopConfig/LoopConfigDialog';
import EdgeConditionDialog from './EdgeCondition/EdgeConditionDialog';
import { getGatewayStore } from '../../Gateway/useGatewayStore';
import W86FloatingPane from '../../W86Components/W86FloatingPane';
import { ComponentFactory } from '../../ProjectCanvas/ComponentRegistry';
import CopyComponentsPane from './CopyComponentsPane';
import { LOOP_THROUGH_LIST_MAX_COMPONENTS } from '../../LoopThroughList/LoopThroughListConstants';

// MUI
import { withStyles } from '@mui/styles';
import { Close, Send } from '@mui/icons-material';
import {
  Autocomplete,
  Button,
  CircularProgress,
  Fade,
  Grid,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from '@mui/material';
import BrandingWatermarkIcon from '@mui/icons-material/BrandingWatermark';

// Zustand
import useCanvasStore from './useCanvasStore';
import useProjectStore from '../../ProjectCanvas/useProjectStore';
import TemplateCanvas from './CanvasVersions/TemplateCanvas';
import {
  PRODUCTION_CANVAS_VIEW_MODE,
  VERSION_CANVAS_VIEW_MODE,
  TEMPLATE_CANVAS_MODE,
  PROGRESS_CANVAS_MODE,
  NOTE_TYPE,
  DEFAULT_CANVAS_MODE,
  PRODUCTION_CANVAS_MODE,
  EDGE_TYPES,
} from '../../../utils/CanvasConstants';
import { progressNodeTypes, prodNodeTypes, versionNodeTypes } from './ReactFlowUtil/CustomNodes/ComponentNodeTypes';
import { initializeComponent } from '../../../utils/ComponentInitializationUtil';
import { ComponentLimitReachedDialog } from '../Dialogs/ComponentLimitReachedDialog';

const styles = {
  controls: {
    left: 'auto',
    right: '16px',
    bottom: '16px',
  },
  minimap: {
    position: 'absolute',
    float: 'left',
    left: '5vw',
  },
  canvas: {
    position: 'absolute!important',
  },
  aiIconBtn: {
    height: '18px',
    width: 'auto',
    paddingRight: '8px',
  },
  gridCenterItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeSelectedButton: {
    height: '18px',
    width: 'auto',
    '&:hover': {
      cursor: 'pointer',
      color: 'rgba(0, 0, 0, 0.65)',
    },
  },
  sendButton: {
    height: '100%',
  },
  sendIcon: {
    height: '20px',
    width: 'auto',
  },
  selectItem: {},
};

const Canvas = props => {
    const nodes = useCanvasStore(s => s.nodes);
    const edges = useCanvasStore(s => s.edges);
    const tier = useProjectStore(s => s.tier);

    const [reactFlowInstance, setReactFlowInstance] = useState({});
    const [showMinimap, setShowMinimap] = useState(false);
    const [showNotes, setShowNotes] = useState(false);
    const [moreComponents, setMoreComponents] = useState(false);
    // this state handles copy menu
    const [copyComponents, setCopyComponents] = useState(false);
    const [loadCopy, setLoadCopy] = useState(false);
    const [workflows, setWorkflows] = useState([]);
    const [workflow, setWorkflow] = useState('');
    const [actionType, setActionType] = useState('copy_to_current_workflow');
    const [selectedComponents, setSelectedComponents] = useState([]);
    const [selectedNotes, setSelectedNotes] = useState([]);
    const [selectedNodes, setSelectedNodes] = useState([]);
    const [isSelecting, setIsSelecting] = useState(false);
    const [showCopyResults, setCopyResults] = useState(false);
    const [copyDestination, setCopyDestination] = useState('');
    const [copyDestinationLink, setCopyDestinationLink] = useState('');
    const [maxComponents, setMaxComponents] = useState(
      tier === 'FREE' || tier === 'FREE_TRIAL' ? 50 : 250
    );
    const [disable, setDisable] = useState(false);

    // Loop config dialog state (for BPMN backward connections)
    const [loopConfigDialogOpen, setLoopConfigDialogOpen] = useState(false);
    const [pendingLoopConnection, setPendingLoopConnection] = useState(null);
    // Loop edit state (for editing existing loop edges)
    const [editingLoopEdge, setEditingLoopEdge] = useState(null);
    const [editingGatewayEdge, setEditingGatewayEdge] = useState(null);
    const [edgeConditionDialogOpen, setEdgeConditionDialogOpen] = useState(false);
    const [pendingGatewayConnection, setPendingGatewayConnection] = useState(null);

    //Check if Notes has been clicked
    const { isNoteClicked } = useContext(CanvasContext);
    const [noteClicked, setNoteClicked] = isNoteClicked ? isNoteClicked : useState(false);

    //Handle Drag and Drop
    const onInit = _reactFlowInstance => {
      setReactFlowInstance(_reactFlowInstance);

      if (props.setReactFlowInstance) {
        props.setReactFlowInstance(_reactFlowInstance);
      }

      if (props.mode === DEFAULT_CANVAS_MODE || props.mode === PRODUCTION_CANVAS_MODE) {
        props.setReactFlowInstance(_reactFlowInstance);
      } else {
        //needs to load elements array
        _reactFlowInstance.fitView();
        setTimeout(() => _reactFlowInstance.fitView(), 300);
      }

      if (props.viewport != null) {
        _reactFlowInstance.setViewport(props.viewport);
      }
    };

    //This will select the component when hovering mouse over node. Doing this so
    //it will trigger a shadow on the component node and open up the component options
    const handleOnNodeMouseEnter = (node, addSelectedNodes) => {
      const isNodeSelected = selectedNodes.some(selectedNode => selectedNode.id === node.id);
      const canSelectMoreNodes = selectedNodes.length <= 1;

      if (node && !isNodeSelected && canSelectMoreNodes && !noteClicked) {
        addSelectedNodes([node.id]);
      }
    };

    //For All Canvas except for Draft canvas
    //Opposite of handleOnNodeMouseEnter, will deselect component when mouse is hovered away
    const handleOnNodeMouseLeave = (node, resetSelectedElements, getNodes, setNodes) => {
      const isNodeSelected =
        node && selectedNodes.some(selectedNode => selectedNode.id === node.id);

      if (isNodeSelected && !isSelecting) {
        deselectedNode(node, getNodes(), setNodes);
      }
    };

    const deselectedNode = (nodeToDeselect, allNodes, setNodeMethod) => {
      setNodeMethod(
        allNodes.map(singleNode => {
          return { ...singleNode, selected: false };
        })
      );
    };

    /**
     *
     * @param selection Object that contains a list nodes and edges that has been selected
     * @param allowCopy True to enable copy feature, false to disable it
     */
    const handleSelectionChange = (selection, allowCopy) => {
      const newSelectedNodes = selection.nodes;
      const shouldOpenCopyMenu =
        (newSelectedNodes.length > 1 || (newSelectedNodes.length === 1 && isSelecting)) &&
        allowCopy;
      const newSelectedComponents = [];
      const selectedNotesArr = [];

      // Single pass through selection.nodes to gather components and notes
      newSelectedNodes.forEach(item => {
        if (item.type === 'component' && !item.data.fromWorkflowPath) {
          newSelectedComponents.push(item.id);
        }
        if (
          item.type === NOTE_TYPE ||
          (item.type === EDGE_TYPES.LINK && item.label === undefined)
        ) {
          selectedNotesArr.push(item.id);
        }
      });

      //If no nodes are selected nothing needs to be done here
      //Closing copy components since nothing is selected
      if (!(selection && newSelectedNodes)) {
        setCopyComponents(false);
        return;
      }

      if (areSetsEqual(newSelectedNodes, selectedNodes)) {
        return;
      }

      setSelectedNodes(newSelectedNodes);
      setCopyComponents(shouldOpenCopyMenu);

      if (newSelectedNodes.length === 0 || !allowCopy) {
        return;
      }

      if (!areSetsEqual(newSelectedComponents, selectedComponents)) {
        setSelectedComponents(newSelectedComponents);
      }

      if (!areSetsEqual(selectedNotesArr, selectedNotes)) {
        setSelectedNotes(selectedNotesArr);
      }
    };

    const navigate = useNavigate();

    if (props.mode === PROGRESS_CANVAS_MODE) {
      return (
        <ReactFlowProvider>
          <ReactFlow
            nodes={props.nodes}
            edges={props.edges}
            nodeTypes={progressNodeTypes}
            edgeTypes={edgeTypes}
            arrowHeadColor={'#868686'}
            onInit={onInit}
            nodesDraggable={false}
            minZoom={CANVAS_MIN_ZOOM_OUT}
            proOptions={proOptions}
            deleteKeyCode={null}
            showMinimap={showMinimap}
            setShowMinimap={setShowMinimap}
            onPaneClick={props.handleCloseRerunModal}
          >
            <Controls className={props.classes.controls} position="bottom-right">
              <Tooltip title={'Toggle Minimap'} placement={'top'}>
                <span>
                  <ControlButton
                    onClick={() => {
                      setShowMinimap(!showMinimap);
                    }}
                  >
                    <BrandingWatermarkIcon />
                  </ControlButton>
                </span>
              </Tooltip>
            </Controls>
            {showMinimap && <CanvasMinimap />}
            <Background variant="dots" gap={16} color="#868686" />
          </ReactFlow>
        </ReactFlowProvider>
      );
    } else if (props.mode === TEMPLATE_CANVAS_MODE || props.isTemplate) {
      return (
        <ReactFlowProvider>
          <TemplateCanvas
            onInit={onInit}
            nodes={props.nodes}
            edges={props.edges}
            mode={props.mode}
            showMinimap={showMinimap}
            setShowMinimap={setShowMinimap}
            switchShowMinimap={() => {
              setShowMinimap(!showMinimap);
            }}
            switchShowNotesProd={() => {
              props.setShowNotesProd(!props.showNotesProd);
            }}
            showNotesProd={props.showNotesProd}
            viewport={props.viewport}
            classes={props.classes}
            handleOnNodeMouseEnter={handleOnNodeMouseEnter}
            handleOnNodeMouseLeave={handleOnNodeMouseLeave}
            handleOnPaneClick={props.handleClosePane}
            handleSelectionChange={handleSelectionChange}
            handleRun={props.handleRun}
          />
        </ReactFlowProvider>
      );
    } else if (
      props.mode === VERSION_CANVAS_VIEW_MODE ||
      props.mode === PRODUCTION_CANVAS_VIEW_MODE
    ) {
      const useNodeTypes = props.mode === VERSION_CANVAS_VIEW_MODE ? versionNodeTypes : prodNodeTypes;
      return (
        <ReactFlowProvider>
          <ReactFlow
            nodes={props.nodes}
            edges={props.edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            nodeTypes={useNodeTypes}
            edgeTypes={edgeTypes}
            arrowHeadColor={'#868686'}
            defaultViewport={props.viewport ? props.viewport : DEFAULT_VIEWPORT}
            onInit={onInit}
            nodesDraggable={false}
            minZoom={CANVAS_MIN_ZOOM_OUT}
            proOptions={proOptions}
            deleteKeyCode={null}
            showMinimap={showMinimap}
            setShowMinimap={setShowMinimap}
          >
            <Controls className={props.classes.controls} position="bottom-right">
              <Tooltip title={'Toggle Minimap'} placement={'top'}>
                <span>
                  <ControlButton
                    onClick={() => {
                      setShowMinimap(!showMinimap);
                    }}
                  >
                    <BrandingWatermarkIcon />
                  </ControlButton>
                </span>
              </Tooltip>
            </Controls>
            {showMinimap && <CanvasMinimap />}
            <Background variant="dots" gap={16} color="#868686" />
          </ReactFlow>
        </ReactFlowProvider>
      );
    } else {
      //using context to update canvas state
      const { edit } = useContext(CanvasContext);
      const [editComponent, setEditComponent] = edit;
      //record last modified value for the canvas
      const { updated, isCanvasLocked } = useContext(CanvasContext);
      const [lastModified, setLastModified] = updated ? updated : useState({});
      const [canvasLocked, setCanvasLocked] = isCanvasLocked ? isCanvasLocked : useState(false);

      //canvas mode
      const [mode, setMode] = useState(props.mode);

      useEffect(() => {
        setMode(props.mode);
      }, [props.mode]);

      // Listen for edit loop edge events from LoopLink component
      useEffect(() => {
        const handleEditLoopEdge = (event) => {
          const { edgeId, source, target, maxIterations, exitCondition } = event.detail;
          setEditingLoopEdge({ edgeId, source, target, maxIterations, exitCondition });
          setLoopConfigDialogOpen(true);
        };

        window.addEventListener('editLoopEdge', handleEditLoopEdge);
        return () => window.removeEventListener('editLoopEdge', handleEditLoopEdge);
      }, []);

      // Listen for edit gateway edge events from GatewayLink component
      useEffect(() => {
        const handleEditGatewayEdge = (event) => {
          const { edgeId, source, target, edgeCondition } = event.detail;
          setEditingGatewayEdge({ edgeId, source, target, edgeCondition });
          setEdgeConditionDialogOpen(true);
        };
        window.addEventListener('editGatewayEdge', handleEditGatewayEdge);
        return () => window.removeEventListener('editGatewayEdge', handleEditGatewayEdge);
      }, []);

      useEffect(() => {
        setMaxComponents(
          tier === 'FREE' || tier === 'FREE_TRIAL' ? 50 : 250
        );
      }, [tier]);

      const [nextComponentToOpen, setNextComponentToOpen] = useState(null);

      useEffect(() => {
        if (nextComponentToOpen && editComponent === null) {
          updateNextComponentIdInEditComponent(nextComponentToOpen);
          setNextComponentToOpen(null);
        }
      }, [nextComponentToOpen, editComponent, reactFlowInstance]);

      const updateNextComponentIdInEditComponent = nextComponentToOpenNode => {
        const nextComponentToOpen = nextComponentToOpenNode.data;
        if (nextComponentToOpen.fromWorkflowPath) {
          let parent = getElements(reactFlowInstance).find(
            x => x.id === nextComponentToOpen.parentId
          );
          setEditComponent(parent.data);
        } else {
          nextComponentToOpen.componentId = nextComponentToOpenNode.id;
          setEditComponent(nextComponentToOpen);
        }
      };

      //Handle links draw
      const onConnect = params => {
        //pause save is here to disable canvas events when component pane is open
        let source = nodes.find(node => params.source == node.id);
        let target = nodes.find(node => params.target == node.id);

        if (mode === DEFAULT_CANVAS_MODE) {
          // If it's an invalid branch path connection, return
          if (isInvalidBranchPathsConnection(source, target, props.setShowErrorSnackbar)) {
            return;
          }

          if (
            isInvalidConvergeThreadsConnection(
              source,
              target,
              nodes,
              edges,
              props.setShowErrorSnackbar
            )
          ) {
            return;
          }

          // If it's an invalid loop through list connection, return
          if (
            isInvalidNestedLoopConnection(
              source,
              target,
              nodes,
              edges,
              props.setShowErrorSnackbar
            )
          ) {
            return;
          }

          // We need to check if the target is a trigger component (i.e: it doesnt have an input)
          // If it is, just return as the user is trying to connect an input of another component
          // To the input of a trigger component
          if (
            source.type !== NOTE_TYPE &&
            target.type !== NOTE_TYPE &&
            target.data &&
            !target.data.hasInput
          )
            return;

          // Check that a link doesn't exist already
          const index = edges.findIndex(e => isSameLink(e, params));
          if (index !== -1) return; // If the link already exists, return

          //this a regular component link
          if (params.source != params.target) {
            if (
              source.data &&
              source.data.hasOutput == true &&
              source.type == 'component' &&
              target.type == 'component'
            ) {
              // Check if this is a backward connection (loop) in BPMN mode
              if (props.useBpmnJoinBehavior && isBackwardConnection(params, nodes, edges)) {
                // Show loop config dialog
                setPendingLoopConnection({ params, source, target });
                setLoopConfigDialogOpen(true);
              } else if (source.data.type === 'gateway') {
                // Gateway source: show edge condition dialog
                setPendingGatewayConnection({ params, source, target });
                setEdgeConditionDialogOpen(true);
              } else {
                // Normal forward connection
                createComponentLink(params, source, target);
              }
            } else if (source.type == NOTE_TYPE) {
              //this is a note link
              if (source.type == NOTE_TYPE && target.type == NOTE_TYPE) {
                createNoteLink(params, source, target);
              }
            }
          }
        } else if (mode === 'SAVE_PAUSED') {
          //preventing links from connecting
          //show a banner
          props.setLinkWarning(true);
        }
      };

      // Handle loop config dialog confirmation (for both create and edit)
      const handleLoopConfigConfirm = async (maxIterations, exitCondition) => {
        if (editingLoopEdge) {
          // Editing existing loop edge
          await updateLoopEdge(editingLoopEdge, maxIterations, exitCondition);
          setEditingLoopEdge(null);
        } else if (pendingLoopConnection) {
          // Creating new loop edge
          const { params, source, target } = pendingLoopConnection;
          createComponentLink(params, source, target, { isLoop: true, maxIterations, exitCondition });
          setPendingLoopConnection(null);
        }
        setLoopConfigDialogOpen(false);
      };

      // Handle loop config dialog close/cancel
      const handleLoopConfigClose = () => {
        setLoopConfigDialogOpen(false);
        setPendingLoopConnection(null);
        setEditingLoopEdge(null);
      };

      // Handle edge condition dialog confirmation (for gateway edges)
      const handleEdgeConditionConfirm = async (edgeCondition) => {
        if (editingGatewayEdge) {
          // Editing existing gateway edge
          await updateGatewayEdge(editingGatewayEdge, edgeCondition);
          setEditingGatewayEdge(null);
        } else if (pendingGatewayConnection) {
          // Creating new gateway edge
          const { params, source, target } = pendingGatewayConnection;
          createComponentLink(params, source, target, { edgeCondition });
          setPendingGatewayConnection(null);
        }
        setEdgeConditionDialogOpen(false);
      };

      const handleEdgeConditionClose = () => {
        setEdgeConditionDialogOpen(false);
        setPendingGatewayConnection(null);
        setEditingGatewayEdge(null);
      };

      // Update existing gateway edge with new edgeCondition
      const updateGatewayEdge = async (edgeData, newEdgeCondition) => {
        if (props.saving) return;
        props.setSaving(true);

        const { edgeId, source, target } = edgeData;

        const updatedEdges = edges.map(edge => {
          if (edge.id === edgeId) {
            return {
              ...edge,
              data: { ...edge.data, edgeCondition: newEdgeCondition },
              style: {
                ...edge.style,
                stroke: newEdgeCondition.isDefault ? '#999999' : '#F5A623',
                strokeDasharray: newEdgeCondition.isDefault ? '5,5' : undefined,
              },
            };
          }
          return edge;
        });
        useCanvasStore.getState().setEdges(updatedEdges);

        const rfiObj = reactFlowInstance.toObject();
        const viewport = {
          x: rfiObj.viewport.x.toFixed(5),
          y: rfiObj.viewport.y.toFixed(5),
          zoom: rfiObj.viewport.zoom.toFixed(5),
        };

        const eventData = {
          sourceComponentId: source,
          targetComponentId: target,
          edgeCondition: newEdgeCondition,
        };

        const saveEvent = createSaveEvent(
          'UPDATE_EDGE',
          props.workflowId,
          viewport,
          getElements(reactFlowInstance),
          eventData
        );

        try {
          const response = await saveCanvasEvent(props.workflowId, saveEvent);
          if (response?.data?.updated) {
            setLastModified(response.data.lastModified);
          }
        } catch (error) {
          console.error('Error updating gateway edge:', error);
        } finally {
          props.setSaving(false);
        }
      };

      // Update existing loop edge with new maxIterations and exitCondition
      const updateLoopEdge = async (loopEdgeData, newMaxIterations, newExitCondition) => {
        if (props.saving) return;
        props.setSaving(true);

        const { edgeId, source, target } = loopEdgeData;

        // Update the edge in the store
        const updatedEdges = edges.map(edge => {
          if (edge.id === edgeId) {
            return {
              ...edge,
              data: {
                ...edge.data,
                maxIterations: newMaxIterations,
                exitCondition: newExitCondition,
              },
            };
          }
          return edge;
        });
        useCanvasStore.getState().setEdges(updatedEdges);

        // Save to backend
        const rfiObj = reactFlowInstance.toObject();
        const viewport = {
          x: rfiObj.viewport.x.toFixed(5),
          y: rfiObj.viewport.y.toFixed(5),
          zoom: rfiObj.viewport.zoom.toFixed(5),
        };

        const eventData = {
          sourceComponentId: source,
          targetComponentId: target,
          isLoop: true,
          maxIterations: newMaxIterations,
          exitCondition: newExitCondition,
        };

        const saveEvent = createSaveEvent(
          'UPDATE_LOOP_EDGE',
          props.workflowId,
          viewport,
          getElements(reactFlowInstance),
          eventData
        );

        try {
          const response = await saveCanvasEvent(props.workflowId, saveEvent);
          if (response?.data?.updated) {
            setLastModified(response.data.lastModified);
          }
        } catch (error) {
          console.error('Error updating loop edge:', error);
        } finally {
          props.setSaving(false);
        }
      };

      // TODO: ======= UPDATED ======
      const createComponentLink = async (params, source, target, loopData = null) => {
        if (!props.saving) {
          props.setSaving(true);
          
          // Check if this is a loop edge or gateway edge
          const isLoopEdge = loopData && loopData.isLoop;
          const isGatewayEdge = loopData && loopData.edgeCondition;

          //style component link
          if (isGatewayEdge) {
            const ec = loopData.edgeCondition;
            params.style = {
              strokeWidth: '3px',
              stroke: ec.isDefault ? '#999999' : '#F5A623',
              strokeDasharray: ec.isDefault ? '5,5' : undefined,
            };
            params.type = EDGE_TYPES.GATEWAYLINK;
            params.markerEnd = { type: 'arrowclosed', color: '#F5A623' };
            params.label = 'gatewayLink';
            params.data = { ...params.data, edgeCondition: ec };
          } else if (isLoopEdge) {
            params.style = {
              strokeWidth: '3px',
              stroke: '#9c27b0',
              strokeDasharray: '5,5',
            };
            params.type = EDGE_TYPES.LOOPLINK;
            params.markerEnd = { type: 'arrowclosed', color: '#9c27b0' };
            params.label = 'loopLink';
            params.data = {
              ...params.data,
              isLoop: true,
              maxIterations: loopData.maxIterations,
              exitCondition: loopData.exitCondition,
            };
          } else {
            params.style = {
              strokeWidth: '3px',
              stroke: source.data.fromWorkflowPath ? '#868686' : source.data.baseColor,
            };
            params.type = EDGE_TYPES.LINK;
            params.markerEnd = { type: 'arrowclosed', color: '#868686' };
            params.label = 'componentLink';
          }
          params.id = uuid();

          //add it to frontend
          useCanvasStore.getState().setEdges([...edges, params]);

          //record viewport
          let rfiObj = reactFlowInstance.toObject();

          let viewport = {
            x: rfiObj.viewport.x.toFixed(5),
            y: rfiObj.viewport.y.toFixed(5),
            zoom: rfiObj.viewport.zoom.toFixed(5),
          };

          let eventData = {
            sourceComponentId: source.id,
            targetComponentId: target.id,
            targetType: target.data.type,
          };
          
          // Only include loop metadata for loop edges
          if (isLoopEdge) {
            eventData.isLoop = true;
            eventData.maxIterations = loopData.maxIterations;
            eventData.exitCondition = loopData.exitCondition;
          }
          // Include edge condition for gateway edges
          if (isGatewayEdge) {
            eventData.edgeCondition = loopData.edgeCondition;
          }

          let saveEvent = createSaveEvent(
            'ADD_LINK',
            props.workflowId,
            viewport,
            getElements(reactFlowInstance),
            eventData
          );

          //send event
          return await saveCanvasEvent(props.workflowId, saveEvent)
            .then(response => {
              if (response && response.data && response.data.updated) {
                setLastModified(response.data.lastModified);
              }
              //insert delay here
              props.setSaving(false);
            })
            .catch(error => {
              console.log(error);
              props.setSaving(false);
            });
        }
      };

      const createNoteLink = async (params, source, target) => {
        if (props.saving) return;

        props.setSaving(true);

        //this is a note, notes can only connect to notes
        params.style = {
          strokeWidth: '3px',
          stroke: 'rgba(0,0,0,0.3)',
        };

        params.type = EDGE_TYPES.LINK;
        params.markerEnd = {
          type: 'arrowclosed',
          color: '#868686',
        };
        params.id = uuid();

        useCanvasStore.getState().setEdges([...edges, params]);

        let rfiObj = reactFlowInstance.toObject();

        let viewport = {
          x: rfiObj.viewport.x.toFixed(5),
          y: rfiObj.viewport.y.toFixed(5),
          zoom: rfiObj.viewport.zoom.toFixed(5),
        };

        let eventData = {
          arrowHeadType: 'arrowclosed',
          id: params.id,
          source: source.id,
          sourceHandle: params.sourceHandle,
          style: {
            strokeWidth: '3px',
            stroke: 'rgba(0,0,0,0.3)',
          },
          target: target.id,
          targetHandle: params.targetHandle,
          type: EDGE_TYPES.LINK,
        };

        let saveEvent = createSaveEvent(
          'ADD_NOTE_LINK',
          props.workflowId,
          viewport,
          getElements(reactFlowInstance),
          eventData
        );

        //send event
        await saveCanvasEvent(props.workflowId, saveEvent)
          .then(response => {
            if (response.data && response.data.updated) {
              setLastModified(response.data.lastModified);
            }
            //insert delay here
            props.setSaving(false);
          })
          .catch(error => {
            console.log(error);
          });
        return;
      };

      const onDragOver = event => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
      };

      const onDrop = event => {
        // moving here to stop drop behaviour in firefox for
        // prod and draft view. For template and ProgressView
        // drag and drop is disabled
        event.preventDefault();

        if (mode === DEFAULT_CANVAS_MODE) {
          if (!props.pauseState) {
            //pause save is here to disable canvas events when component pane is open

            //find out current position
            const position = reactFlowInstance.screenToFlowPosition({
              x: event.clientX,
              y: event.clientY,
            });

            // If it's a note
            if (event.dataTransfer.getData('application/reactflow') === NOTE_TYPE) {
              //this is a note, send create note event
              addNewNote(event, position); //
            } else {
              //this is adding a new component to the canvas
              addNewComponent(event, position);
            }
          }
        }
      };

      const addNewComponent = async (event, position) => {
        if (props.saving) return; // If already saving, return

        props.setSaving(true);

        //check for the amount of components on the canvas already
        if (getNodesComponentCount() < maxComponents) {
          //use data from sidebar to determine type of the node
          const type = event.dataTransfer.getData('type');
          if (
            type === 'loop_through_list' &&
            getLoopThroughListCount() >= LOOP_THROUGH_LIST_MAX_COMPONENTS
          ) {
            props.setShowErrorSnackbar(
              `Maximum ${LOOP_THROUGH_LIST_MAX_COMPONENTS} Split into Threads components per workflow`
            );
            props.setSaving(false);
            return;
          }
          //create a node using template
          const template = ComponentFactory[type];
          //this a new node for the frontend
          const newNode = {
            id: uuid(),
            type: 'component',
            position,
            data: {
              type: type,
              label: template.displayName,
              displayName: template.displayName,
              logo: template.logo,
              hasInput: template.hasInput ? template.hasInput : false,
              hasOutput: template.hasOutput ? template.hasOutput : false,
              baseColor: template.baseColor,
              schemaVersion: 2,
            },
          };

          //add it to the nodes
          useCanvasStore.getState().setNodes([...useCanvasStore.getState().nodes, newNode]);

          let rfiObj = reactFlowInstance.toObject();

          let viewport = {
            x: rfiObj.viewport.x.toFixed(5),
            y: rfiObj.viewport.y.toFixed(5),
            zoom: rfiObj.viewport.zoom.toFixed(5),
          };

          //create event for the new component
          let eventData = {
            componentId: newNode.id,
            type: newNode.data.type,
            viewData: {
              x: position.x,
              y: position.y,
            },
          };

          let saveEvent = createSaveEvent(
            'ADD_COMPONENT',
            props.workflowId,
            viewport,
            getElements(reactFlowInstance),
            eventData
          );
          //send event
          return await saveCanvasEvent(props.workflowId, saveEvent)
            .then(response => {
              if (response.data && response.data.updated) {
                setLastModified(response.data.lastModified);

                initializeComponent(type, eventData.componentId);
              }
              //insert delay here
              props.setSaving(false);
            })
            .catch(error => {
              console.log(error);
            });
        } else setMoreComponents(true); //there are too many components on the canvas, show dialog
      };

      const addNewNote = async (event, position) => {
        if (props.saving) return; // If already saving, return

        props.setSaving(true);

        //grab data
        const noteData = {
          width: event.dataTransfer.getData('width'),
          height: event.dataTransfer.getData('height'),
          color: event.dataTransfer.getData('color'),
          content: event.dataTransfer.getData('content'),
        };

        //create note
        const newNote = {
          id: uuid(),
          type: NOTE_TYPE,
          position,
          data: {
            content: noteData.content,
            color: noteData.color,
            width: parseInt(noteData.width) ? parseInt(noteData.width) : 400,
            height: parseInt(noteData.height) ? parseInt(noteData.height) : 400,
          },
        };

        //add it to the nodes
        useCanvasStore.getState().setNodes([...nodes, newNote]);

        let rfiObj = reactFlowInstance.toObject();

        let viewport = {
          x: rfiObj.viewport.x.toFixed(5),
          y: rfiObj.viewport.y.toFixed(5),
          zoom: rfiObj.viewport.zoom.toFixed(5),
        };

        let eventData = {
          note: newNote,
        };
        //create event for the new note
        let saveEvent = createSaveEvent(
          'ADD_NOTE',
          props.workflowId,
          viewport,
          getElements(reactFlowInstance),
          eventData
        );

        //send event
        await saveCanvasEvent(props.workflowId, saveEvent)
          .then(response => {
            if (response.data && response.data.updated) {
              setLastModified(response.data.lastModified);
            }
            if (!props.showNotes) {
              props.setShowNotes(true);
            }
            //insert delay here
            props.setSaving(false);
          })
          .catch(error => {
            console.log(error);
          });
      };

      const getNodesComponentCount = () => {
        return nodes.filter(c => c.type === 'component').length;
      };

      const getLoopThroughListCount = () => {
        return nodes.filter(
          node => node.type === 'component' && node.data?.type === 'loop_through_list'
        ).length;
      };

      const handleNavigateIntoComponent = (event, node) => {
        // Prevent component interaction when canvas is locked
        if (canvasLocked) {
          return;
        }

        if (node && node.data && node.data.isTemp === true) {
          return;
        }

        if (node && node.type === NOTE_TYPE && node.data.content === undefined) {
          //"not saving anything as content is not loaded";
          return;
        }

        if (
          mode === 'SAVE_PAUSED' &&
          editComponent != null &&
          editComponent.componentId !== node.id
        ) {
          //preventing links from connecting
          //show a banner
          props.setNavigationWarning(true);
          return;
        }

        new Promise((fulfill, reject) => {
          useCanvasStore.getState().setNodes(reactFlowInstance.getNodes());
          fulfill();
        }).then(async () => {
          if (node.type === 'component') {
            if (editComponent) {
              useProjectStore.setState({ savedComponent: false });
              setNextComponentToOpen(node);
              setEditComponent(null);
            } else {
              updateNextComponentIdInEditComponent(node);
            }
          }
        });
      };

      const handleSelectionStart = selection => {
        setIsSelecting(true);
        setCopyResults(false);
      };

      const handleSelectionEnd = selection => {
        setIsSelecting(false);
      };

      const handleBuildAIFromSelectedNotes = async () => {
        if (!selectedNotes || selectedNotes.length <= 0) {
          setLoadCopy(false);
          return;
        }

        closeCopy(); // Close the menu
        setCanvasLocked('Collecting notes for AI'); // Set to locked

        // Set border around notes sent to AI
        const updatedNodes = getCanvasWithAINotesBorderSet(nodes, new Set(selectedNotes));
        useCanvasStore.getState().setNodes(updatedNodes);

        const dto = {
          projectId: useProjectStore.getState().project_id,
          noteIds: selectedNotes,
        };

        // Send request to create ai session for the notes
        const url = `project-service/project-ai/assistant/create_ai_session_for_notes`;
        const json = await send_request(url, dto, null, 'POST');

        if (json && json.data) {
          const { projectId, chatSessionId, threadId } = json.data;
          navigate(
            `/project/canvas/${projectId}?chatSession=${chatSessionId}&threadId=${threadId}&startBuild=true`
          );
        }

        setCanvasLocked(false);
      };

      //For Draft Canvas Only
      const handleOnNodeMouseLeaveDraft = (node, resetSelectedElements, getNodes, setNodes) => {
        // Exit early if the node is undefined/null, not part of the selected nodes, or if the user is currently selecting.
        if (
          !node ||
          !selectedNodes.some(selectedNode => selectedNode.id === node.id) ||
          isSelecting
        ) {
          return;
        }

        // Determine if the node is of type NOTE_TYPE.
        const isNote = node.type === NOTE_TYPE;

        // Reset the selected elements if the node is not a note or if it is a note but hasn't been clicked.
        if (!isNote || (isNote && !noteClicked)) {
          resetSelectedElements();
        }
      };

      //these methods handle selection actions
      const requestCopy = async () => {
        if (props.saving) return; // If saving, return
        setLoadCopy(true);

        // This is a notes -> ai call
        if (actionType === 'build_from_selected_notes') {
          await handleBuildAIFromSelectedNotes();
          return;
        }

        // Handle delete selected components
        if (actionType === 'delete_selected') {
          const selectedSet = [...new Set(selectedComponents)];
          const selectedNoteIds = selectedNotes.map(note => note.id || note);

          if (selectedSet.length > 0 || selectedNoteIds.length > 0) {
            props.setSaving(true);
            // Get all items to delete (components + notes)
            const allIdsToDelete = [...selectedSet, ...selectedNoteIds];

            // Filter out the deleted items from canvas
            let result = [...nodes, ...edges];
            result = result.filter(node => {
              // Filter out selected components and notes
              if (selectedSet.includes(node.id) || selectedNoteIds.includes(node.id)) {
                return false;
              }
              // Filter out edges connected to deleted items
              if (node.source && allIdsToDelete.includes(node.source)) {
                return false;
              }
              if (node.target && allIdsToDelete.includes(node.target)) {
                return false;
              }
              return true;
            });

            useCanvasStore.getState().setNodesAndEdges(result);

            let rfiObj = reactFlowInstance.toObject();
            let viewport = {
              x: rfiObj.viewport.x.toFixed(5),
              y: rfiObj.viewport.y.toFixed(5),
              zoom: rfiObj.viewport.zoom.toFixed(5),
            };

            // Delete each component individually using DELETE_COMPONENT event
            // (same approach as single component delete in DraftCanvasComponent)
            for (const componentId of selectedSet) {
              const componentNode = nodes.find(n => n.id === componentId);
              const componentType = componentNode?.data?.type || '';

              let eventData = {
                componentId: componentId,
                type: componentType,
              };

              let saveEvent = createSaveEvent(
                'DELETE_COMPONENT',
                props.workflowId,
                viewport,
                getElements(reactFlowInstance),
                eventData
              );

              await saveCanvasEvent(props.workflowId, saveEvent)
                .then(response => {
                  if (response.data && response.data.updated) {
                    setLastModified(response.data.lastModified);
                  }
                })
                .catch(error => {
                  console.log(error);
                });
            }

            // Delete each note individually using DELETE_NOTE event
            for (const noteId of selectedNoteIds) {
              let eventData = {
                noteId: noteId,
              };

              let saveEvent = createSaveEvent(
                'DELETE_NOTE',
                props.workflowId,
                viewport,
                getElements(reactFlowInstance),
                eventData
              );

              await saveCanvasEvent(props.workflowId, saveEvent)
                .then(response => {
                  if (response.data && response.data.updated) {
                    setLastModified(response.data.lastModified);
                  }
                })
                .catch(error => {
                  console.log(error);
                });
            }

            props.setSaving(false);
          }

          closeCopy();
          return;
        }

        let copyToId = '';

        if (actionType == 'copy_to_current_workflow') copyToId = props.workflowId;
        else {
          copyToId = workflow.projectId;
        }

        const selectedSet = [...new Set(selectedComponents)]; // Remove any duplicates

        if (actionType == 'copy_to_current_workflow') {
          const selectedLoopThroughListCount = selectedSet.reduce((count, componentId) => {
            const componentNode = nodes.find(node => node.id === componentId);
            return count + (componentNode?.data?.type === 'loop_through_list' ? 1 : 0);
          }, 0);

          if (
            getLoopThroughListCount() + selectedLoopThroughListCount >
            LOOP_THROUGH_LIST_MAX_COMPONENTS
          ) {
            props.setShowErrorSnackbar(
              `Maximum ${LOOP_THROUGH_LIST_MAX_COMPONENTS} Split into Threads components per workflow`
            );
            setLoadCopy(false);
            return;
          }
        }

        if (selectedSet.length > 0 || selectedNotes.length > 0) {
          let rfiObj = reactFlowInstance.toObject();

          let viewport = {
            x: rfiObj.viewport.x.toFixed(5),
            y: rfiObj.viewport.y.toFixed(5),
            zoom: rfiObj.viewport.zoom.toFixed(5),
          };

          let eventData = {
            projectId: copyToId,
            components: selectedSet,
            notes: selectedNotes,
          };

          //create event for the new note
          let saveEvent = createSaveEvent(
            'COPY_SELECTION',
            props.workflowId,
            viewport,
            getElements(reactFlowInstance),
            eventData
          );

          //send event
          await saveCanvasEvent(props.workflowId, saveEvent)
            .then(response => {
              if (actionType == 'copy_to_current_workflow' || workflow == props.workflowId) {
                let components = response ? response.data.copiedSelection : null;
                let notes = response ? response.data.copiedNotes : null;

                if (components) {
                  let newDiagram = useProjectStore.getState().readDiagram(components);

                  //add it to the nodes/edges
                  useCanvasStore.getState().addNodes(extractNodes(newDiagram));
                  useCanvasStore.getState().addEdges(extractEdges(newDiagram));
                }

                if (notes) {
                  notes.map(note => {
                    if (note.type == NOTE_TYPE) {
                      delete note.style;
                      delete note.source;
                      delete note.sourceHandle;
                      delete note.target;
                      delete note.targetHandle;
                      delete note.arrowHeadType;
                    } else if (note.type == EDGE_TYPES.LINK) {
                      if (note.data?.label) {
                        let newData = note.data.label;
                        note.data = newData;
                      } else delete note.data;
                      delete note.position;
                    }
                  });
                  //add it to the nodes/edges
                  useCanvasStore.getState().addNodes(extractNodes(notes));
                  useCanvasStore.getState().addEdges(extractEdges(notes));
                }
              }
              //insert delay here
              props.setSaving(false);

              if (response.data && response.data.updated) {
                setLastModified(response.data.lastModified);
              }
              if (actionType == 'copy_to_another_workflow') {
                setCopyDestinationLink(workflow.projectId);
                setCopyDestination(workflow.projectName);
                setCopyResults(true);
              }
            })
            .catch(error => {
              closeCopy();
              console.log(error);
            });

          //handle updating canvas if we create a new component
        }

        closeCopy();
      };

      const handleSelectWorkflowType = async action => {
        setActionType(action);
        setDisable(true);

        //load workflows here to select where to copy
        if (action == 'copy_to_another_workflow' && workflows.length == 0) {
          await send_request(`project-service/project/get_workflow_names/${props.projectId}`)
            .then(res => {
              if (res && res.status == 200) {
                setWorkflows(res.data);
              }
            })
            .catch(e => {
              console.log(e);
              closeCopy();
            });
        } else setLoadCopy(false);
      };

      const handleSelectWorkflow = async choice => {
        if (choice == 'select_workflow' && actionType == 'copy_to_another_workflow')
          setLoadCopy(true);
        else setLoadCopy(false);

        setWorkflow(choice);

        setDisable(false);
      };

      const closeCopy = () => {
        setWorkflow('select_workflow');
        setActionType('copy_to_current_workflow');
        setCopyComponents(false);
        setWorkflows([]);
        setSelectedComponents([]);
        setSelectedNotes([]);
        setLoadCopy(false);
      };

      const handleSelectionContextMenu = event => {
        event.preventDefault();

        if (selectedComponents.length > 0 || selectedNotes.length > 0) {
          setCopyComponents(true); //open copy components dialog
        }
      };

      const connectionStatus = useCallback(
        connection => {
          return isValidConnection(connection, nodes, edges, props.useBpmnJoinBehavior);
        },
        [nodes, edges, props.useBpmnJoinBehavior]
      );

      return (
        <>
          <ReactFlowProvider>
            <CanvasWithRFInstance
              handleNavigateIntoComponent={handleNavigateIntoComponent}
              onConnect={onConnect}
              onDrop={onDrop}
              onInit={onInit}
              handleSelectionChange={handleSelectionChange}
              handleSelectionContextMenu={handleSelectionContextMenu}
              handleSelectionStart={handleSelectionStart}
              handleSelectionEnd={handleSelectionEnd}
              onDragOver={onDragOver}
              canvasLocked={canvasLocked}
              viewport={props.viewport}
              classes={props.classes}
              showNotes={props.showNotes}
              setShowNotes={props.setShowNotes}
              isInlineEditing={props.isInlineEditing}
              setIsInlineEditing={props.setIsInlineEditing}
              showMinimap={showMinimap}
              setShowMinimap={setShowMinimap}
              isValidConnection={connectionStatus}
              handleOnNodeMouseEnter={handleOnNodeMouseEnter}
              handleOnNodeMouseLeave={handleOnNodeMouseLeaveDraft}
              handleOnPaneClick={props.handleClosePaneButtonClick}
            />
            <CopyComponentsPane
              copyComponents={copyComponents}
              setCopyComponents={setCopyComponents}
              showCopyResults={showCopyResults}
              setCopyResults={setCopyResults}
              actionType={actionType}
              setActionType={setActionType}
              canvasLocked={canvasLocked}
              loadCopy={loadCopy}
              workflow={workflow}
              workflows={workflows}
              handleSelectWorkflowType={handleSelectWorkflowType}
              handleSelectWorkflow={handleSelectWorkflow}
              requestCopy={requestCopy}
              selectedNotes={selectedNotes}
              copyDestination={copyDestination}
              copyDestinationLink={copyDestinationLink}
              classes={props.classes}
            />
          </ReactFlowProvider>

          <ComponentLimitReachedDialog
            open={moreComponents}
            maxComponentLimit={maxComponents}
            onCloseClick={() => {
              setMoreComponents(false);
              props.setSaving(false);
            }}
            onUpgradeClick={() => {
              navigate('/organization');
              setMoreComponents(false);
              props.setSaving(false);
            }}
          />

          {/* Loop Configuration Dialog (BPMN backward connections and editing) */}
          <LoopConfigDialog
            isOpen={loopConfigDialogOpen}
            onClose={handleLoopConfigClose}
            onConfirm={handleLoopConfigConfirm}
            initialMaxIterations={editingLoopEdge?.maxIterations || 10}
            initialExitCondition={editingLoopEdge?.exitCondition || null}
            isEditMode={!!editingLoopEdge}
            projectId={useProjectStore.getState().project_id}
            sourceComponentId={pendingLoopConnection?.source?.data?.componentId || editingLoopEdge?.source}
          />

          {/* Edge Condition Dialog (gateway edge configuration) */}
          <EdgeConditionDialog
            isOpen={edgeConditionDialogOpen}
            onClose={handleEdgeConditionClose}
            onConfirm={handleEdgeConditionConfirm}
            initialEdgeCondition={editingGatewayEdge?.edgeCondition || null}
            projectId={useProjectStore.getState().project_id}
            sourceComponentId={pendingGatewayConnection?.source?.id || editingGatewayEdge?.source}
            gatewayType={(() => {
              try {
                const srcId = pendingGatewayConnection?.source?.id || editingGatewayEdge?.source;
                if (!srcId) return null;
                return getGatewayStore(srcId)?.getState()?.data?.gatewayType || null;
              } catch { return null; }
            })()}
          />
        </>
      );
    }
};

export default withStyles(styles)(Canvas);
